import React, { useState, useEffect, useMemo } from 'react';
import { BaseReportTable, ReportFooter } from './BaseReportTable';
import { BaseColumn } from '@/components/common/table/BaseTable';
import { reportsApi, ReportParams } from '@/api/endpoints/reports';

interface SubTasksReportProps {
  dateFilter: string;
  customDateRange: { from: string; to: string };
}

export const SubTasksReport: React.FC<SubTasksReportProps> = ({ dateFilter, customDateRange }) => {
  const [reportData, setReportData] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [dateFilter, customDateRange]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: ReportParams = {
        dateType: dateFilter as any,
      };

      if (dateFilter === 'custom' && customDateRange.from && customDateRange.to) {
        params.fromDate = customDateRange.from;
        params.toDate = customDateRange.to;
      }

      const response = await reportsApi.getSubTasksReport(params);
      setReportData(response.data.data.subTasks || []);
      setTotals(response.data.data.totals || {});
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define columns with proper alignment and widths
  const columns: BaseColumn[] = useMemo(() => [
    { 
      key: "subTask", 
      label: "SUB-TASK", 
      sortable: true, 
      visible: true,
      width: "200px",
      minWidth: "200px",
      render: (item) => item.subTask || 'N/A'
    },
    { 
      key: "task", 
      label: "TASK", 
      sortable: true, 
      visible: true,
      width: "200px",
      minWidth: "200px",
      render: (item) => item.task || 'N/A'
    },
    { 
      key: "totalEstimatedTime", 
      label: "TOTAL EST. TIME", 
      sortable: true, 
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (item) => item.totalEstimatedTime || 0
    },
    { 
      key: "totalActualTime", 
      label: "TOTAL TIME TAKEN", 
      sortable: true, 
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (item) => item.totalActualTime || 0
    },
  ], []);

  // Calculate totals for footer
  const footerTotals = useMemo(() => ({
    count: reportData.length,
    totalEstimatedTime: reportData.reduce((sum, item) => sum + (item.totalEstimatedTime || 0), 0),
    totalActualTime: reportData.reduce((sum, item) => sum + (item.totalActualTime || 0), 0),
  }), [reportData]);

  // Column config for footer alignment
  const columnConfig = useMemo(() => [
    {
      key: "subTask",
      width: "200px",
      minWidth: "200px",
      label: "SUB-TASK"
    },
    {
      key: "task",
      width: "200px",
      minWidth: "200px",
      label: "TASK"
    },
    {
      key: "totalEstimatedTime",
      width: "80px",
      minWidth: "80px",
      label: "TOTAL EST. TIME"
    },
    {
      key: "totalActualTime",
      width: "80px",
      minWidth: "80px",
      label: "TOTAL TIME TAKEN"
    }
  ], []);

  return (
    <div className="h-full bg-white flex flex-col">
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full">
          <BaseReportTable
            data={reportData}
            columns={columns}
            loading={loading}
            emptyMessage="No sub-task data found"
            selectable={false}
            showFilters={false}
            sortable={true}
            onSort={(columnKey) => console.log("Sort by:", columnKey)}
            dropdownConfig={{}}
            selectedFilters={{}}
            getItemId={(item) =>
              item._id || item.subTaskId || `subtask-${Math.random()}`
            }
            containerClassName="h-full"
            rowClassName="border-b border-gray-100 hover:bg-gray-50"
          />
        </div>
      </div>

      <ReportFooter 
        data={reportData}
        columnConfig={columnConfig}
        totals={footerTotals}
      />
    </div>
  );
};